/**
 * Suivi des sessions d'exercice.
 *
 * Un exercice n'est pas « terminé » : le produit n'a pas cette notion, rien
 * dans le Studio ne déclare la fin d'un exercice. Mesurer un
 * `exercise_completed` reviendrait à inventer une donnée. Ce qui est
 * observable et utile, c'est **le temps réellement passé dessus** — donc une
 * session ouverte au chargement et fermée quand l'exercice cesse d'être le
 * morceau actif.
 *
 * L'état vit au niveau du module et non dans React : le Studio est monté en
 * permanence et les exercices s'y succèdent sans démontage, il n'y a donc pas
 * de cycle de vie de composant sur lequel s'accrocher.
 *
 * # Ce qui ferme une session, et pourquoi il en fallait plus
 *
 * La session ne se fermait que par un autre chargement ou par `pagehide` :
 * `durationMs` mesurait donc le temps **jusqu'à l'exercice suivant** et non le
 * temps passé sur celui-ci. Dix minutes de travail puis une heure passée
 * ailleurs dans l'application rendaient une séance d'une heure dix. Deux
 * sorties s'y ajoutent : le retour dans `/learn`, qui appelle
 * {@link endExerciseSession} depuis la coque de la section, et le départ de la
 * page, délégué à {@link import("./departure").resumableOnDeparture}.
 *
 * # Pourquoi le départ ne suffisait toujours pas
 *
 * Le premier correctif n'avait ajouté que la moitié du contrat : il fermait la
 * session sur la mise en arrière-plan sans jamais la rouvrir. Il échangeait
 * ainsi une perte totale contre une troncature systématique — basculer trente
 * secondes sur une autre application puis revenir travailler une demi-heure ne
 * rapportait que les trente premières secondes. `resumableOnDeparture` tient
 * les deux temps, et c'est la seule raison pour laquelle la plomberie est
 * passée chez lui.
 *
 * # Ce que la réouverture n'émet pas
 *
 * Elle rouvre l'enveloppe, jamais `exercise_started`. Ce dernier compte des
 * **démarrages d'exercice**, et c'est le palier sur lequel se referme
 * l'entonnoir de /learn : le ré-émettre à chaque retour au premier plan
 * ferait apparaître plus de démarrages que d'ouvertures, et le taux de
 * conversion de la pré-séance passerait au-dessus de 100 %. Une séance coupée
 * en deux rend donc deux `exercise_session_ended` pour un seul
 * `exercise_started`, ce qui est exactement ce qui s'est passé.
 */
import { enqueue } from "./client";
import { resumableOnDeparture } from "./departure";
import type { VirtuosaEvent } from "./events";

/** Ce qui identifie une session, et suffit à la rouvrir telle quelle. */
interface ExerciseSubject {
  exerciseId: string;
  /**
   * Résolu une fois pour toutes à l'ouverture, jamais relu ensuite.
   *
   * Il se déduit de la route, et au retour au premier plan la route est
   * `/studio` : le relire ferait perdre son parcours à toute session rouverte,
   * c'est-à-dire précisément aux séances les plus longues.
   */
  programId?: string;
  /**
   * Objet qui « possède » la session (le contrôleur de morceau chargé pour
   * cet exercice). Comparer les identités permet de détecter que le Studio
   * est passé à autre chose, sans avoir à câbler un événement de sortie sur
   * chacun des cinq chemins de chargement de morceau.
   */
  owner: object;
}

interface ExerciseSession extends ExerciseSubject {
  startedAt: number;
}

let current: ExerciseSession | null = null;

/**
 * Le départ, le retour, et la mémoire du sujet entre les deux.
 *
 * La réouverture n'émet **pas** `exercise_started` : ce palier ferme
 * l'entonnoir de `/learn`, et le rejouer à chaque retour au premier plan
 * porterait la conversion de la pré-séance au-dessus de 100 %.
 */
const departure = resumableOnDeparture<ExerciseSubject>({
  close: () => {
    endExerciseSession("pagehide");
  },
  reopen: openSession,
});

/** `/learn/<programId>/<exerciseId>` → `<programId>`, sinon `undefined`. */
function programIdFromPathname(pathname: string): string | undefined {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);

  return segments[0] === "learn" && segments.length >= 3 ? segments[1] : undefined;
}

function emit(event: VirtuosaEvent): void {
  // Passe par `enqueue` et non par `track` de `index.ts` : ce module est
  // importé *par* la barrière publique, y remonter créerait un cycle.
  enqueue(event);
}

/** Ouvre l'enveloppe sur ce sujet. Ne dit rien : seul le chronomètre repart. */
function openSession(subject: ExerciseSubject): void {
  current = { ...subject, startedAt: Date.now() };
}

/**
 * Ouvre une session d'exercice, en fermant la précédente s'il y en avait une.
 *
 * @param owner Contrôleur de morceau chargé pour cet exercice.
 */
export function startExerciseSession(
  exerciseId: string,
  owner: object,
  attributes: { difficulty?: number; category?: string | null } = {},
): void {
  endExerciseSession("switched");

  // Lu ici et pas plus tard : au moment du chargement, l'utilisateur est
  // encore sur `/learn/<programId>/<exerciseId>` (la navigation vers le
  // studio n'intervient qu'après l'await).
  const programId =
    typeof window === "undefined" ? undefined : programIdFromPathname(window.location.pathname);

  // Après la fermeture de la précédente, faite en tête de fonction : cette
  // fermeture oublie le sujet, et l'ordre inverse jetterait celui-ci.
  const subject: ExerciseSubject = { exerciseId, programId, owner };

  departure.opened(subject);
  openSession(subject);

  emit({
    name: "exercise_started",
    properties: {
      exerciseId,
      programId,
      difficulty: attributes.difficulty,
      category: attributes.category ?? undefined,
    },
  });
}

/** Ferme la session en cours, si elle existe. */
export function endExerciseSession(reason: "switched" | "pagehide" | "left"): void {
  if (current === null) {
    return;
  }

  const session = current;
  current = null;

  /*
    Une sortie qui n'est pas un départ est définitive : on a quitté l'exercice,
    pas la page. Garder le sujet ferait rouvrir une session au prochain retour
    au premier plan, alors que plus aucun morceau d'exercice n'est chargé — une
    séance fantôme, dont la durée compterait le temps passé ailleurs.

    L'enveloppe tranche seule : c'est elle qui déclenche la fermeture d'un
    départ, donc elle sait laquelle en est un. Le motif n'a pas à la renseigner.
  */
  departure.closed();

  emit({
    name: "exercise_session_ended",
    properties: {
      exerciseId: session.exerciseId,
      programId: session.programId,
      durationMs: Date.now() - session.startedAt,
      reason,
    },
  });
}

/**
 * Ferme la session si le morceau actif n'est plus celui de l'exercice — le
 * cas quand l'utilisateur charge une pièce du catalogue ou importe un fichier
 * en cours de route.
 */
export function endExerciseSessionUnlessOwnedBy(owner: object | null): void {
  if (current !== null && current.owner !== owner) {
    endExerciseSession("left");
  }
}
